import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
// Tabela reusada sem alteração do mapa Google.
import TablePerPattern from '../MapaFornecedor/TablePerPattern';

// Tipo de análise → rota do relatório de insumos.
const REPORT_ROUTES = {
    'Por padrão': 'web.supplies.reportSuppliesByPattern',
    'Por fase de obra': 'web.supplies.reportSuppliesByStage',
    'Por Incorporadora': 'web.supplies.reportSuppliesByIncorporator',
};

/**
 * Diálogo com a tabela de insumos do Mapa de Fornecedores (Mapbox).
 *
 * Equivalente ao MapaFornecedor/TableViewer.jsx do mapa Google, com duas diferenças:
 * usa o Dialog do PrimeReact (o legado é uma div `.table-background` posicionada à mão,
 * que não cobre a página de mapa em tela cheia) e o download do XLSX envia o payload
 * **normalizado** (nomes de cidade/bairro/insumo, ids de incorporadora), como faz
 * TablePerPattern.preparePayload. O TableViewer legado posta os filtros crus — arrays
 * de {value,label} — onde BuildingSuppliesReportController::applyQueryFilters espera
 * escalares, e o download quebra quando há cidade selecionada.
 *
 * `filters` continua indo cru para o TablePerPattern, que faz a própria preparação.
 *
 * O Dialog do PrimeReact 6 não renderiza nada enquanto `visible` é falso, então o
 * TablePerPattern remonta (e refaz a consulta com os filtros atuais) a cada abertura.
 */
export default function SupplyTableModal({ visible, type, filters, buildings, onClose }) {
    const [downloading, setDownloading] = useState(false);

    const preparePayload = () => ({
        ...filters,
        status: ['Ativo'],
        // Com formas desenhadas, o relatório é recortado pelos empreendimentos exibidos.
        buildings: filters.there_are_shapes ? buildings : [],
        cities: (filters.cities || []).map((item) => item.label),
        neighborhoods: (filters.neighborhoods || []).map((item) => item.label),
        supplies: (filters.supplies || []).map((item) => item.label),
        incorporators: (filters.incorporators || []).map((item) => item.value),
        pattern: filters.pattern?.value ? [filters.pattern.value] : [],
        construction_status: filters.construction_status?.value ? [filters.construction_status.value] : [],
    });

    const downloadFile = async () => {
        const routeName = REPORT_ROUTES[type?.label];
        if (!routeName) return;

        setDownloading(true);

        try {
            const response = await axios.post(
                route(routeName),
                { ...preparePayload(), format: 'excel' },
                { responseType: 'blob' }
            );

            const contentType = response.headers['content-type'];
            if (response.status !== 200 || contentType !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
                return;
            }

            const blob = new Blob([response.data], { type: contentType, encoding: 'UTF-8' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = response.headers['content-disposition'].split('=')[1];
            link.click();
            window.URL.revokeObjectURL(link.href);
        } catch (e) {
            console.error('[SupplyTableModal] erro ao baixar o relatório:', e);
        } finally {
            setDownloading(false);
        }
    };

    const footer = (
        <div>
            <Button
                label={downloading ? 'Gerando…' : 'Baixar XLSX'}
                icon={downloading ? 'pi pi-spin pi-spinner' : 'pi pi-download'}
                onClick={downloadFile}
                disabled={downloading}
            />
            <Button label="Fechar" icon="pi pi-times" className="p-button-text" onClick={onClose} />
        </div>
    );

    return (
        <Dialog
            header={`Insumos — ${type?.label || ''}`}
            visible={visible}
            onHide={onClose}
            footer={footer}
            modal
            maximizable
            className="fornecedor-mapbox-dialog"
            style={{ width: '85vw' }}
            contentStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
        >
            {/* TablePerPattern.preparePayload reatribui as chaves do próprio payload
                (cities vira array de nomes, pattern vira array…). Passamos uma cópia
                rasa para que `filters` continue cru para o download do XLSX. */}
            <TablePerPattern
                type={type}
                payload={{ ...filters }}
                grandParentState={{ empreendimentos: buildings.map((id) => ({ id_legado: id })) }}
            />
        </Dialog>
    );
}
